import React, { useMemo, useState } from 'react'
import { graphql, useFragment } from 'react-relay'

import {
  differenceInCalendarDays,
  differenceInCalendarWeeks,
  differenceInCalendarMonths,
  getHours,
} from 'date-fns'

import { format, utcToZonedTime, getTimezoneOffset } from 'date-fns-tz'
import enUS from 'date-fns/locale/en-US'
import enGB from 'date-fns/locale/en-GB'

import { YStack, XStack } from 'tamagui'
import { Paragraph, Icon, Text } from '@newComponents'

import { EventScreenDateTime_event$key } from './__generated__/EventScreenDateTime_event.graphql'

export const EventScreenDateTime = ({
  event,
}: {
  event: EventScreenDateTime_event$key
}) => {
  const data = useFragment(
    graphql`
      fragment EventScreenDateTime_event on Event {
        startAt
        endAt

        timeZone
      }
    `,
    event
  )

  const [displayEventTimeZone, setDisplayEventTimeZone] = useState(false)

  const displayTimeZone = useMemo(() => {
    try {
      if (!displayEventTimeZone) {
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

        if (userTimeZone && userTimeZone !== 'Etc/Unknown') {
          return userTimeZone
        }
      }
    } catch (err) {}

    return data.timeZone
  }, [data, displayEventTimeZone])

  const startDate = useMemo(() => {
    return utcToZonedTime(new Date(data.startAt), displayTimeZone)
  }, [data, displayTimeZone])

  const endDate = useMemo(() => {
    return utcToZonedTime(new Date(data.endAt), displayTimeZone)
  }, [data, displayTimeZone])

  const formatOptions = useMemo(() => {
    const timeZoneOffset = getTimezoneOffset(
      displayTimeZone,
      new Date(data.startAt)
    )

    const locale = timeZoneOffset < -2 * 60 * 60 * 1000 ? enUS : enGB

    return { locale, timeZone: displayTimeZone }
  }, [data, displayTimeZone])

  return (
    <XStack
      gap='$4'
      alignItems='center'
      cursor='pointer'
      userSelect='none'
      onPress={(event) => {
        event.preventDefault()
        event.stopPropagation()

        setDisplayEventTimeZone((value) => !value)
      }}
    >
      <CalendarIcon startDate={startDate} formatOptions={formatOptions} />

      <DateTime
        startDate={startDate}
        endDate={endDate}
        formatOptions={formatOptions}
      />
    </XStack>
  )
}

const CalendarIcon = ({
  startDate,
  formatOptions,
}: {
  startDate: Date
  formatOptions: { locale: typeof enUS | typeof enGB; timeZone: string }
}) => {
  return (
    <YStack
      width='$10'
      height='$10'
      alignItems='center'
      borderRadius='$2'
      boxShadow='$boxShadowLight'
    >
      <Paragraph
        size='$2'
        fontWeight='$6'
        textTransform='uppercase'
        backgroundColor='$grey4'
        width='100%'
        textAlign='center'
        paddingVertical={2}
        borderTopLeftRadius='$2'
        borderTopRightRadius='$2'
        marginHorizontal={-1}
      >
        {format(startDate, 'MMM', formatOptions)}
      </Paragraph>

      <YStack
        flex={1}
        alignItems='center'
        justifyContent='center'
        borderWidth={1}
        borderTopWidth={0}
        borderColor='$grey4'
        borderBottomLeftRadius='$2'
        borderBottomRightRadius='$2'
        width='100%'
        backgroundColor='$grey0'
      >
        <Paragraph
          fontWeight='$5'
          // letterSpacing={1}
          textAlign='center'
          // paddingLeft={1}
        >
          {format(startDate, 'd', formatOptions)}
        </Paragraph>
      </YStack>
    </YStack>
  )
}

const DateTime = ({
  startDate,
  endDate,
  formatOptions,
}: {
  startDate: Date
  endDate: Date
  formatOptions: { locale: typeof enUS | typeof enGB; timeZone: string }
}) => {
  const startEndDiff = useMemo(() => {
    return differenceInCalendarDays(endDate, startDate)
  }, [startDate, endDate])

  if (startEndDiff === 0 || (startEndDiff === 1 && getHours(endDate) < 10)) {
    return (
      <SameDay
        startDate={startDate}
        endDate={endDate}
        formatOptions={formatOptions}
      />
    )
  }

  return (
    <DifferentDays
      startDate={startDate}
      endDate={endDate}
      formatOptions={formatOptions}
    />
  )
}

const SameDay = ({
  startDate,
  endDate,
  formatOptions,
}: {
  startDate: Date
  endDate: Date
  formatOptions: { locale: typeof enUS | typeof enGB; timeZone: string }
}) => {
  const dayDiff = useMemo(() => {
    return differenceInCalendarDays(startDate, new Date())
  }, [startDate])

  const timeDisplay = useMemo(() => {
    return (
      <Paragraph size='$4' fontWeight='$5' color='$grey8'>
        <Text size='$4' color='$grey7'>
          from
        </Text>{' '}
        {format(startDate, `h:mma`, formatOptions)}{' '}
        <Text size='$4' color='$grey7'>
          to
        </Text>{' '}
        {format(endDate, `h:mma`, formatOptions)}{' '}
        <Text size='$4' color='$grey7'>
          {format(startDate, 'zzz', formatOptions)}
        </Text>
      </Paragraph>
    )
  }, [startDate, endDate, formatOptions])

  if (dayDiff === 0) {
    return (
      <YStack gap='$1'>
        <Paragraph size='$5' fontWeight='$5'>
          Today
        </Paragraph>

        {timeDisplay}
      </YStack>
    )
  }

  if (dayDiff === 1) {
    return (
      <YStack gap='$1'>
        <Paragraph size='$5' fontWeight='$5'>
          Tomorrow
        </Paragraph>

        {timeDisplay}
      </YStack>
    )
  }

  if (dayDiff === -1) {
    return (
      <YStack gap='$1'>
        <Paragraph size='$5' fontWeight='$5'>
          Yesterday
        </Paragraph>

        {timeDisplay}
      </YStack>
    )
  }

  return (
    <YStack gap='$1'>
      <Paragraph size='$5' fontWeight='$5'>
        {format(startDate, `EEEE, MMMM do`, formatOptions)}
      </Paragraph>

      {timeDisplay}
    </YStack>
  )
}

const DifferentDays = ({
  startDate,
  endDate,
  formatOptions,
}: {
  startDate: Date
  endDate: Date
  formatOptions: { locale: typeof enUS | typeof enGB; timeZone: string }
}) => {
  const monthDiff = useMemo(() => {
    return differenceInCalendarMonths(endDate, startDate)
  }, [startDate, endDate])

  if (monthDiff === 0) {
    return (
      <YStack gap='$1'>
        <Paragraph size='$5' fontWeight='$5'>
          {format(startDate, `EEEE, MMMM do`, formatOptions)}{' '}
          <Text size='$4' color='$grey7'>
            to
          </Text>{' '}
          {format(endDate, `do`, formatOptions)}
        </Paragraph>

        <Paragraph size='$4' fontWeight='$5' color='$grey8'>
          <Text size='$4' color='$grey7'>
            from
          </Text>{' '}
          {format(startDate, `h:mma`, formatOptions)}{' '}
          <Text size='$4' color='$grey7'>
            to
          </Text>{' '}
          {format(endDate, `h:mma`, formatOptions)}{' '}
          <Text size='$4' color='$grey7'>
            {format(startDate, 'zzz', formatOptions)}
          </Text>
        </Paragraph>
      </YStack>
    )
  }

  return (
    <YStack gap='$1'>
      <Paragraph size='$5' fontWeight='$5'>
        {format(startDate, `MMM do`, formatOptions)}{' '}
        <Text size='$4' color='$grey7'>
          to
        </Text>{' '}
        {format(endDate, `MMM do`, formatOptions)}
      </Paragraph>

      <Paragraph size='$4' fontWeight='$5' color='$grey8'>
        <Text size='$4' color='$grey7'>
          from
        </Text>{' '}
        {format(startDate, `h:mma`, formatOptions)}{' '}
        <Text size='$4' color='$grey7'>
          to
        </Text>{' '}
        {format(endDate, `h:mma`, formatOptions)}{' '}
        <Text size='$4' color='$grey7'>
          {format(startDate, 'zzz', formatOptions)}
        </Text>
      </Paragraph>
    </YStack>
  )
}
